import type { AiCapabilityPage } from "../schema.js";
import { LIVE, OWNER_ADMIN } from "./_shared.js";

/**
 * `COL-CLOSE-REOPEN` (@UC0211) — trang **redirect** (US-AIV2-0049): chat KHÔNG hỏi ô, KHÔNG có nút Đồng ý,
 * chỉ một câu giải thích + nút mở đúng màn hình `/groups/:groupId/collections` (ADR-0009 bất biến 5).
 * US-AIV2-0110 S10 (`@UC0259`): trước trỏ trang chủ nhóm `/groups/:groupId`; nay tab "Quản lý quỹ" (danh sách đợt thu) — màn chi tiết đợt thu cần `collectionId` mà engine chưa điền được.
 * Cửa mobile trang này chịu trách nhiệm (hai cửa chốt / mở lại đợt thu). *
 * 🔴 `redirect` vì chốt đợt là **cột mốc có hệ quả tiền**: đóng đợt thì đợt THÔI NHẬN khoản đóng (dues giữ nguyên bit-for-bit — `close()`
 * KHÔNG sinh khoản nợ nào; GUIDE-SCREEN-TRUTH 15/10 sửa câu cũ "biến suất chưa đóng thành khoản còn nợ"),
 * và mở lại một đợt đã chốt đụng đúng chỗ PR #… từng làm vỡ bất biến settlement. Người bấm phải nhìn thấy
 * danh sách ai đã/chưa đóng ngay trên màn hình đó — thứ mà một thẻ trong chat không thay được.
 */
export const COL_CLOSE_REOPEN = {
  id: "COL-CLOSE-REOPEN",
  uc: "@UC0211",
  tier: "redirect",
  status: LIVE,
  roles: OWNER_ADMIN, // US-AIV2-tourguide-0008 (@UC0362 · review #1238 H1): thủ quỹ/thành viên KHÔNG thấy việc này ở màn đích (cờ app `canManageMembers` / sửa đợt = chủ nhóm + quản trị) ⇒ hẹp vai cho đúng app.
  flags: [],
  slots: [],
  llm: {
    description: "Đóng hoặc mở lại một ĐỢT THU (không phải đóng nhóm): đóng thì thôi nhận báo đã chuyển, mở lại thì nhận tiếp.",
    examples: [
      "chốt đợt thu",
      "chot dot thu",
      "đóng đợt thu tháng này",
      "dong dot thu thang nay",
      "mở lại đợt thu",
      "mo lai dot thu",
      "đợt thu xong rồi chốt nhé",
      "kết thúc đợt quỹ lớp",
      "ket thuc dot quy lop",
      "chốt kỳ",
      "đóng đợt",
      "mở lại đợt",
      // ——— 0105: thêm cho đủ 40 ———
      "chốt sổ đợt thu này lại",
      "chot so dot thu nay lai",
      "khoá đợt thu không cho báo nữa",
      "khoa dot thu khong cho bao nua",
      "đợt thu này thôi không nhận nữa",
      "dot thu nay thoi khong nhan nua",
      "mở lại lần thu tháng trước",
      "mo lai lan thu thang truoc",
      "lỡ chốt sớm quá, mở lại giúp mình",
      "lo chot som qua mo lai giup minh",
      "cho đóng đợt thu dã ngoại",
      "cho dong dot thu da ngoai",
      "muốn chốt đợt thu thì làm sao?",
      "muon chot dot thu thi lam sao",
      "đóng lần thu quỹ lớp",
      "dong lan thu quy lop",
      "mở lại đợt đã chốt được không",
      "mo lai dot da chot duoc khong",
      /*
       * 🔴 0105 vòng 2 — CẶP BỊ THAY. Vòng 1 đưa vào `"dừng nhận báo đã chuyển của đợt này"` lấy chữ
       * từ CHÍNH mô tả trang (*"đóng thì thôi nhận báo đã chuyển"*). Đo được: bản không dấu rơi sang
       * `PAY-CONFIRM-DECIDE`, và model gạch chân ô `quyetDinh` = `"xác nhận"` — ô của trang kia.
       *
       * Cụm *"nhận báo đã chuyển"* là từ vựng LÕI của trang xác nhận lời khai; mượn nó làm câu mẫu ở
       * đây là dạy Sổ hai trang chung một từ vựng. Mô tả giữ nguyên cụm ấy (nó mô tả đúng HỆ QUẢ của
       * việc đóng đợt), nhưng câu MẪU phải nói bằng động từ của trang này: ĐÓNG / KHOÁ đợt.
       */
      "khoá đợt này lại không nhận thêm nữa",
      "khoa dot nay lai khong nhan them nua",
      "chốt đợt thu quý 3",
      "chot dot thu quy 3",
      "đợt thu đủ người rồi, đóng lại nhé",
      "dot thu du nguoi roi dong lai nhe",
      "bật lại đợt thu cho một bạn nộp muộn",
      "bat lai dot thu cho mot ban nop muon",
      "kết thúc lần thu này",
      "ket thuc lan thu nay",
    ],
  },
  screen: "/groups/:groupId/collections",
  screenShows: "collection_close_reopen",
  endpoints: [
    "PATCH /funds/{fundId}/collections/{collectionId}/close",
    "PATCH /funds/{fundId}/collections/{collectionId}/reopen",
  ],
  facts: [],
  templates: {
    // 🔴 GUIDE-SCREEN-TRUTH (G-1, @UC0211): đúng hành vi `close()` — không sinh nợ; KHÔNG hứa "không nhận thêm khoản đóng" (review #1409 M-1: cửa ghi nhận/tự khai không kiểm `collection.status` — hộp thoại app `collection_edit_stop_message` nói sai, nợ riêng).
    redirect:
      "Mình mở danh sách đợt thu nhé — bấm vào đợt đó rồi bấm nút sửa: ở đó có nút ngừng thu và kích hoạt lại. Ngừng đợt không xoá khoản nào và không biến ai thành nợ — những khoản đã ghi vẫn giữ nguyên, và bạn kích hoạt lại được sau. Nên xem lại ai đã đóng, ai chưa một lượt trước khi bấm.",
  },
  journey: { after: ["READ-COLLECTIONS", "DEBT-REMIND"] },
  help: { title: "Chốt đợt thu", tryNow: "chốt đợt thu" },
} satisfies AiCapabilityPage;
